/**
 * EntityChip — clickable pill for entity ids (PR F · §F.1.5).
 *
 * The seven lens views all render their own entity-pill variants today.
 * This unifies the visual + behaviour. Clicking navigates to the entity
 * sheet by default; override via `onClick`. Domain dot colour comes from
 * `domain.color` in the manifest (a CSS-var token like `--d-money`).
 * Without manifest context, the dot is muted but the chip stays usable.
 */
import type { JSX } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import { navigateToEntity } from '../state/hashRouter.js';

export interface EntityChipProps {
  entityId: string;
  manifest?: AtlasManifest;
  size?: 'sm' | 'md';
  /** Custom click handler; receives entityId. Defaults to entity navigate. */
  onClick?: (entityId: string) => void;
}

const DEFAULT_DOT = 'var(--text3)';

export function EntityChip({
  entityId,
  manifest,
  size = 'md',
  onClick,
}: EntityChipProps): JSX.Element {
  const dotColor = resolveDotColor(entityId, manifest);
  const handleClick = (): void => {
    if (onClick) onClick(entityId);
    else navigateToEntity(entityId);
  };
  return (
    <button
      type="button"
      className="entity-chip"
      data-size={size}
      onClick={handleClick}
    >
      <span
        className="entity-chip-dot"
        aria-hidden="true"
        style={{ background: dotColor }}
      />
      <span className="entity-chip-label">{entityId}</span>
    </button>
  );
}

function resolveDotColor(entityId: string, manifest?: AtlasManifest): string {
  if (!manifest) return DEFAULT_DOT;
  const entity = manifest.entities.find((e) => e.id === entityId);
  if (!entity) return DEFAULT_DOT;
  const domain = manifest.domains.find((d) => d.id === entity.domain);
  if (!domain || !domain.color) return DEFAULT_DOT;
  // domain.color is a token like '--d-money'; wrap in var().
  return `var(${domain.color})`;
}
